import { a3Copy } from "@/components/assignments/a3-copy";
import { AssignmentDraftGuard } from "@/components/assignments/assignment-draft-guard";
import { WorkBox, WorkBoxHost } from "@/components/assignments/coursework/WorkBoxHost";
import type { LearnerSessionAssignment, SessionWorkspace } from "@/lib/api/sessionPortal";
import { formatInCourseZone } from "@/lib/session-portal";
import { HandInWork } from "./HandInWork";

const DAY = { weekday: "short", month: "short", day: "numeric" } as const;

function learnerWork(workspace: SessionWorkspace): LearnerSessionAssignment[] {
  return (workspace.plan.unit?.assignments ?? []).filter((item): item is LearnerSessionAssignment => "state" in item);
}

/**
 * Hand in, the learner's box on the session page (board 4): every piece of this
 * session's work opens in place. The server picks the work whose act is the
 * page's one filled act (`primary_work_id` with `open_session_activity`); every
 * other act stays quiet, and an open sheet owns the page's one interruption.
 */
export function HandInBox({ workspace, onRetry, quiet = false }: { workspace: SessionWorkspace; onRetry: () => void; quiet?: boolean }) {
  const { plan, viewer, course, session } = workspace;
  if (plan.coursework_state === undefined) return null;
  const work = learnerWork(workspace);
  const filled = !quiet && viewer.primary_action === "open_session_activity" ? viewer.primary_work_id : null;
  const sessionWords = [session.session_no != null ? a3Copy.session2(session.session_no) : null, formatInCourseZone(session.starts_at, course.timezone, DAY)].filter(Boolean).join(", ");
  return (
    <AssignmentDraftGuard><section id="hand-in" data-session-task="hand-in" aria-label={a3Copy.handIn} className="scroll-mt-20">
      {plan.coursework_state === "unavailable" ? (
        <WorkBox box={{ kind: "fact", id: "session-work-read", label: a3Copy.workForThisSession, job: a3Copy.readTheSessionSNativeWork, owner: { system: "imam" }, fold: "never", detail: () => "", read: { status: "failed", sentence: a3Copy.yourWorkForThisSessionCouldNotBeRead, retry: onRetry } }} />
      ) : work.length === 0 ? (
        <p className="text-ui text-muted-foreground">{a3Copy.noWorkIsSetForThisSession}</p>
      ) : (
        <WorkBoxHost id="session-hand-in" journey="session-hand-in" order={work.map(item => `hand-in-${item.id}`)}>
          {work.map((item, index) => (
            <div key={item.id} className={index > 0 ? "border-t border-rule pt-5" : undefined}>
              <HandInWork
                assignmentId={item.id}
                title={item.title}
                timezone={course.timezone}
                termOpen={course.term_open}
                termName={course.term_name}
                pastDue={item.past_due}
                sessionWords={sessionWords}
                lineLabel={item.grade_item_label}
                filled={item.id === filled}
              />
            </div>
          ))}
        </WorkBoxHost>
      )}
    </section></AssignmentDraftGuard>
  );
}
